import { cn } from '@/lib/utils';
import type { ZXCVBNResult } from 'zxcvbn';
import { MINIMUM_PASSWORD_STRENGTH } from '../settings.schemas';

/** zxcvbn scores 0-4; one segment per score so "very weak" still shows. */
const SEGMENTS = [0, 1, 2, 3, 4];

const SCORE_LABELS = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'];

const SCORE_COLORS = [
  'bg-destructive',
  'bg-destructive',
  'bg-amber-500',
  'bg-green-500',
  'bg-green-600',
];

interface PasswordStrengthMeterProps {
  result: ZXCVBNResult | null;
}

/**
 * Strength bar with zxcvbn's feedback. Renders nothing until a result is
 * available so the layout does not jump while the library loads.
 */
export function PasswordStrengthMeter({ result }: PasswordStrengthMeterProps) {
  if (!result) return null;

  const { score, feedback } = result;
  const passes = score >= MINIMUM_PASSWORD_STRENGTH;
  // zxcvbn warnings have no trailing period while suggestions do.
  const hints = [
    feedback.warning,
    ...(passes ? [] : feedback.suggestions.slice(0, 1)),
  ]
    .filter(Boolean)
    .map((hint) => (/[.!?]$/.test(hint) ? hint : `${hint}.`));

  return (
    <div className="w-full space-y-1" aria-live="polite">
      <div className="flex gap-1">
        {SEGMENTS.map((segment) => (
          <div
            key={segment}
            className={cn(
              'bg-muted h-1.5 flex-1 rounded-full transition-colors',
              score >= segment && SCORE_COLORS[score],
            )}
          />
        ))}
      </div>
      <p
        className={cn(
          'text-xs',
          passes ? 'text-muted-foreground' : 'text-destructive',
        )}
      >
        <span className="font-medium">{SCORE_LABELS[score]}</span>
        {hints.length > 0 && ` — ${hints.join(' ')}`}
      </p>
    </div>
  );
}
